import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { Sidebar, navItems, adminItems } from './Sidebar';
import { cn } from '../../lib/utils';
import { useDarkMode } from '../../hooks/useDarkMode';
import { useAuth } from '../../contexts/AuthContext';
import { Moon, Sun, Bell, Search, Settings, ChevronUp, Activity, LogOut } from 'lucide-react';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { getInitials, getAvatarColor } from '../../lib/utils';

export function MainLayout() {
  const { isDark, toggle: toggleDarkMode } = useDarkMode();
  const { user, isAdmin, logout } = useAuth();
  const [adminOpen, setAdminOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const adminRef = useRef(null);
  const userMenuRef = useRef(null);

  // Close popups when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (adminRef.current && !adminRef.current.contains(e.target)) setAdminOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    if (adminOpen || userMenuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [adminOpen, userMenuOpen]);

  return (
    <div className="min-h-screen bg-background transition-colors">
      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Main content — offset by sidebar on desktop */}
      <div className="md:ml-60 flex flex-col min-h-screen">

        {/* Desktop top header */}
        <header className="top-header hidden md:flex">
          <div className="flex-1 max-w-sm">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Rechercher..."
                className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-border bg-muted/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition"
              />
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={toggleDarkMode} className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" title={isDark ? 'Mode clair' : 'Mode sombre'}>
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button className="relative p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
              <Bell size={18} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary rounded-full" />
            </button>
            <div className="flex items-center gap-2.5 ml-1 pl-3 border-l border-border">
              <div className="text-right hidden lg:block">
                <p className="text-sm font-semibold leading-none text-foreground">{user?.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{user?.email}</p>
              </div>
              <Avatar className="h-8 w-8">
                <AvatarFallback className={cn('text-xs font-semibold', getAvatarColor(user?.name))}>
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>
        </header>

        {/* Mobile top bar */}
        <header className="md:hidden flex items-center justify-between px-4 h-12 border-b border-border bg-card sticky top-0 z-30 shadow-sm">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <Activity size={15} className="text-white" />
            </div>
            <span className="font-extrabold text-foreground text-sm tracking-tight">SalePulse</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            {/* User avatar + menu */}
            <div ref={userMenuRef} className="relative">
              <button
                onClick={() => setUserMenuOpen(o => !o)}
                className="ml-1"
              >
                <Avatar className="h-7 w-7">
                  <AvatarFallback className={cn('text-[10px] font-bold', getAvatarColor(user?.name))}>
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 bg-card border border-border rounded-xl shadow-xl w-52 z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="text-sm font-semibold text-foreground leading-tight">{user?.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{user?.email}</p>
                  </div>
                  <div className="p-1">
                    <button
                      onClick={() => { logout(); setUserMenuOpen(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                    >
                      <LogOut size={15} />
                      Se déconnecter
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 pb-24 md:pb-8">
          <Outlet />
        </main>
      </div>

      {/* ── Mobile bottom navigation ── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 safe-area-inset-bottom">

        {/* Admin popup panel */}
        {adminOpen && (
          <div
            ref={adminRef}
            className="absolute bottom-full left-0 right-0 bg-card border border-border rounded-t-2xl shadow-2xl"
          >
            <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-border">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Administration</p>
              <button onClick={() => setAdminOpen(false)} className="p-1 rounded-md text-muted-foreground hover:bg-muted">
                <ChevronUp size={16} />
              </button>
            </div>
            <div className="p-2">
              {adminItems.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setAdminOpen(false)}
                  className={({ isActive }) => cn(
                    'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors',
                    isActive ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                  )}
                >
                  <item.icon size={18} />
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center h-16">
          {/* All nav items */}
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setAdminOpen(false)}
              className={({ isActive }) => cn(
                'flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors min-w-0 px-0.5',
                isActive ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              {({ isActive }) => (
                <>
                  <div className={cn('p-1 rounded-lg transition-colors', isActive && 'bg-primary/10')}>
                    <item.icon size={17} />
                  </div>
                  <span className="text-[9px] font-medium leading-none truncate w-full text-center">
                    {item.label.split(' ')[0]}
                  </span>
                </>
              )}
            </NavLink>
          ))}

          {/* Admin / Settings button */}
          {isAdmin ? (
            <button
              onClick={() => setAdminOpen(!adminOpen)}
              className={cn(
                'flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors min-w-0 px-0.5',
                adminOpen ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              <div className={cn('p-1 rounded-lg transition-colors', adminOpen && 'bg-primary/10')}>
                <Settings size={17} />
              </div>
              <span className="text-[9px] font-medium leading-none">Admin</span>
            </button>
          ) : (
            <NavLink
              to="/settings"
              className={({ isActive }) => cn(
                'flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors min-w-0 px-0.5',
                isActive ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              {({ isActive }) => (
                <>
                  <div className={cn('p-1 rounded-lg transition-colors', isActive && 'bg-primary/10')}>
                    <Settings size={17} />
                  </div>
                  <span className="text-[9px] font-medium leading-none">Réglages</span>
                </>
              )}
            </NavLink>
          )}
        </div>
      </nav>
    </div>
  );
}
